// External Dependencies
import {
  type ReactElement, useCallback, useMemo,
} from 'react';
import { lighten } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import ListItemButton, { type ListItemButtonProps } from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import styled from 'styled-components';
import type { OverridableComponent } from '@mui/material/OverridableComponent';
import type { SvgIconTypeMap } from '@mui/material/SvgIcon';

// Local Dependencies
import EnhancedMenuItemLeftIcon from './EnhancedMenuItemLeftIcon';

// Local Typings
interface StyledListItemButtonProps {
  $isActive?: boolean;
  $isFullWidth?: boolean;
}
interface Props {
  icon?: OverridableComponent<SvgIconTypeMap<{}, 'svg'>>;
  isActive?: StyledListItemButtonProps['$isActive'];
  isFullWidth?: StyledListItemButtonProps['$isFullWidth'];
  onClick?: () => void;
  secondaryText?: string | ReactElement;
  text?: string | ReactElement;
  to?: string;
}

// Local Variables
const StyledListItemButton = styled(ListItemButton)<
  StyledListItemButtonProps & ListItemButtonProps
>(({
  $isActive,
  $isFullWidth,
  theme,
}) => ({
  '& .MuiListItemText-colorTextSecondary': {
    [theme.breakpoints.down('md')]: {
      fontSize: '0.7rem',
    },
  },
  '& .MuiListItemText-primary': {
    [theme.breakpoints.down('md')]: {
      fontSize: '0.9rem',
    },
  },
  '& .MuiListItemText-secondary': {
    color: $isActive ? lighten(theme.palette.navMenuItemText, 0.15) : 'initial',
  },

  borderRadius: theme.shape.borderRadius,
  color: $isActive
    ? theme.palette.prestoPrimaryDark
    : theme.palette.text.primary,
  margin: theme.spacing(1),
  padding: theme.spacing(0.5, 1.5, 0.5, 1),
  width: $isFullWidth ? '100%' : undefined,
}));

// Component Definition
const EnhancedMenuItem = ({
  icon,
  isActive,
  isFullWidth = false,
  onClick,
  secondaryText = '',
  text,
  to,
  ...otherProps
}: Props): JSX.Element => {
  const navigate = useNavigate();

  // We move the navigate function here to construct semantic HTML in the nav
  const handleClick = useCallback(() => {
    // The onClick closes the Drawer when the user clicks/presses a nav menu item
    onClick?.();

    if (to) {
      navigate(to);
    }
  }, [navigate, onClick, to]);

  const styledListButtonItemProps: StyledListItemButtonProps = useMemo(() => ({
    $isActive: isActive,
    $isFullWidth: isFullWidth,
  }), [isActive, isFullWidth]);

  return (
    <StyledListItemButton
      {...styledListButtonItemProps}
      onClick={handleClick}
      onKeyPress={handleClick}
      role="menuitem"
      selected={isActive}
      tabIndex={0}
      {...otherProps}
    >
      {icon && (
        <EnhancedMenuItemLeftIcon
          icon={icon}
          isActive={isActive}
        />
      )}
      <ListItemText
        primary={text}
        secondary={secondaryText}
        slotProps={{
          secondary: {
            component: 'div',
          },
        }}
      />
    </StyledListItemButton>
  );
};

export default EnhancedMenuItem;
